import React, { useEffect, useRef, useState } from "react";
import "./About.css";
import AOS from "aos";
import "aos/dist/aos.css";

/* Counter hook */
function useCounter(end, duration = 2000, started = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!started) return;

    let startTime = null;

    const step = (ts) => {
      if (!startTime) startTime = ts;

      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setCount(Math.floor(eased * end));

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [started, end, duration]);

  return count;
}

const strengths = [
  "Modular OT & Critical Care Solutions",
  "In-house Design & Manufacturing",
  "Sterile Controlled Environments",
  "Compliance with Global Standards",
  "End-to-End Project Execution",
];

const turnkey = [
  {
    icon: "🧠",
    title: "Planning & Execution",
    desc: "We conceptualize, design, develop, assemble and deploy modular healthcare systems with seamless coordination.",
    anim: "fade-right",
  },
  {
    icon: "🏭",
    title: "Infrastructure & Skills",
    desc: "In-house manufacturing, premium raw materials and skilled engineers ensure durability, safety and performance.",
    anim: "fade-up",
  },
  {
    icon: "🏥",
    title: "Best-in-Class Solutions",
    desc: "Advanced modular systems for OT, ICU, PICU, NICU and recovery care units, built to global standards.",
    anim: "fade-left",
  },
];

const statCards = [
  { icon: "👨‍⚕️", end: 130, suffix: "+", label: "Doctors Served" },
  { icon: "🏥", end: 100, suffix: "+", label: "Hospitals" },
  { icon: "🧪", end: 170, suffix: "+", label: "OT's" },
  { icon: "🏆", end: 12, suffix: "+", label: "Awards" },
];

function StatCard({ s, started }) {
  const count = useCounter(s.end, 2000, started);

  return (
    <div className="ab-stat-card">
      <div className="ab-stat-badge">{s.icon}</div>
      <h3 className="ab-stat-num">
        {count}
        {s.suffix}
      </h3>
      <p className="ab-stat-label">{s.label}</p>
      <div className="ab-stat-glow" />
    </div>
  );
}

function About() {
  const [started, setStarted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const statsRef = useRef(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      easing: "ease-out-cubic",
      once: true,
    });
  }, []);

  /* SEO Schema */
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Wellness Electromedical Systems Pvt Ltd",
      url: "https://www.wellnessmodularot.com",
      logo: "https://www.wellnessmodularot.com/bg-tab.png",
      description:
        "Leading modular OT manufacturer in India providing ICU, NICU, PICU and hospital infrastructure solutions.",
      address: {
        "@type": "PostalAddress",
        addressCountry: "India",
      },
    });

    document.head.appendChild(script);

    return () => document.head.removeChild(script);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.3 },
    );

    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, []);

  /* Mobile slider for turnkey cards */
  useEffect(() => {
    const slider = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % turnkey.length);
    }, 3200);

    return () => clearInterval(slider);
  }, []);

  return (
    <section
      className="about-section"
      id="about"
      aria-label="About Wellness Electromedical Systems Pvt Ltd modular operation theatre manufacturer India"
    >
      {/* SEO Hidden */}
      <div className="seo-hidden">
        <h2>
          Modular Operation Theatre Manufacturer in India | ICU NICU PICU
          Solutions
        </h2>
        <p>
          Wellness Electromedical Systems Pvt Ltd is a leading modular OT
          manufacturer in India providing modular operation theatre, ICU, NICU,
          PICU, recovery room and hospital infrastructure solutions.
        </p>
      </div>

      {/* Intro */}
      <div className="ab-container">
        <div className="ab-content" data-aos="fade-right">
          <span className="ab-tag">About WEMS</span>

          <h2 className="ab-heading">
            Leading <span>Modular OT Manufacturer in India</span>
          </h2>

          <p>
            Wellness Electromedical Systems Pvt Ltd. (WEMS) is a trusted modular
            operation theatre manufacturer in India specializing in Modular OT,
            ICU, NICU, PICU, clean rooms, recovery rooms and turnkey hospital
            infrastructure solutions.
          </p>

          <p>
            With 10+ years of experience, WEMS delivers custom healthcare
            infrastructure projects for hospitals, clinics and medical
            institutions across India with international quality standards.
          </p>

          <p>
            Explore our <a href="/modular-ot-services">Modular OT Services</a> and complete
            healthcare infrastructure solutions.
          </p>
        </div>

        {/* Right card */}
        <div className="ab-visual" data-aos="fade-left">
          <div className="ab-card">
            <div className="ab-card-rotate-border" />
            <div className="ab-card-inner">
              <h4>Our Strengths</h4>
              <ul>
                {strengths.map((item, i) => (
                  <li key={i} style={{ animationDelay: `${i * 0.12}s` }}>
                    <span className="ab-check">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="ab-stats-grid" data-aos="fade-up" ref={statsRef}>
        {statCards.map((item, i) => (
          <StatCard key={i} s={item} started={started} />
        ))}
      </div>
      {/* Turnkey */}
      <div className="ab-turnkey">
        <div className="ab-section-header" data-aos="fade-up">
          <span className="ab-tag">Our Expertise</span>
          <h3>Turn Key Solutions for Healthcare</h3>
          <p>
            Complete healthcare infrastructure from concept planning to final
            deployment with unmatched quality and precision.
          </p>
        </div>

        <div className="ab-tk-cards">
          {turnkey.map((item, i) => (
            <div
              className={`ab-tk-card tk-mobile-slide ${
                i === currentSlide ? "active" : ""
              }`}
              key={i}
              data-aos={item.anim}
              data-aos-delay={i * 150}
            >
              <div className="ab-tk-icon">{item.icon}</div>
              <div className="ab-tk-line" />
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
              <div className="ab-tk-glow" />
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
    </section>
  );
}

export default About;
